// Wrapper fetch minimal. Pose les en-têtes JSON + credentials (refresh cookie)
// et gère le format d'erreur standardisé `{ error: { code, message, details? } }`.
//
// Intercepteur spécial : si le backend renvoie 403 avec le code
// `PASSWORD_CHANGE_REQUIRED`, on pose le flag dans le store d'auth pour que
// le router redirige vers `/password/change`. L'erreur est quand même levée
// pour que le composant appelant puisse arrêter sa logique métier.

export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiFailure extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  constructor(status: number, body: ApiError) {
    super(body.message);
    this.status = status;
    this.code = body.code;
    if (body.details !== undefined) {
      this.details = body.details;
    }
  }
}

interface RequestOptions {
  accessToken?: string;
  /**
   * Interne : marque une requête comme déjà rejouée après un silent refresh.
   * Empêche toute récursion : si le 401 revient malgré un refresh réussi,
   * on abandonne et on propage l'erreur (le user sera redirigé login).
   */
  _isRetry?: boolean;
}

/**
 * Bridge avec le store Zustand d'auth, exposé par `App.tsx` au mount sous
 * `window.__crmAuthStore`. Évite un import direct qui créerait un cycle
 * `api.ts` ↔ `stores/auth.ts`.
 */
interface AuthStoreBridge {
  setPasswordChangeRequired: (v: boolean) => void;
  setAuthenticated: (res: AuthenticatedResponse) => void;
  clear: () => void;
  getAccessToken: () => string | null;
}

function readStore(): AuthStoreBridge | null {
  if (typeof window === 'undefined') return null;
  return (window as unknown as { __crmAuthStore?: AuthStoreBridge }).__crmAuthStore ?? null;
}

function markPasswordChangeRequired(): void {
  readStore()?.setPasswordChangeRequired(true);
}

// ---------------------------------------------------------------------------
// Silent refresh + mutex
// ---------------------------------------------------------------------------

/**
 * Promise partagée entre toutes les requêtes concurrentes qui tombent en
 * 401 simultanément. Garantit un SEUL appel à `/auth/refresh` même si 20
 * requêtes échouent au même moment (sinon l'anti-vol de rotation côté
 * backend voit N refreshes avec le même hash cookie → détection de vol →
 * révocation de toutes les sessions). Cf SECURITY.md §2.1 rotation.
 *
 * Réinitialisée en `finally` pour qu'un refresh ultérieur (après une autre
 * période d'inactivité) parte d'un état propre.
 */
let refreshPromise: Promise<void> | null = null;

async function doSilentRefresh(): Promise<void> {
  const store = readStore();
  if (!store) throw new Error('Auth store not ready');

  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const refreshed = await api.post<AuthenticatedResponse>(
          '/api/v1/auth/refresh',
          {},
          { _isRetry: true }, // skipper l'intercepteur sur le refresh lui-même
        );
        store.setAuthenticated(refreshed);
      } catch (err) {
        // Refresh échoué : session invalidée côté serveur (inactivité > 1h,
        // rôle changé, session révoquée par l'admin, cookie manquant…).
        // On purge le store côté client et on redirige sur /login via
        // `window.location.assign` pour réinitialiser proprement tout l'état
        // React (y compris les stores Zustand non-persistés).
        store.clear();
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          window.location.assign('/login');
        }
        throw err;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

// Les routes d'auth qui NE doivent PAS déclencher l'intercepteur (sinon
// boucle infinie ou comportement incohérent) :
//   - /auth/login, /auth/refresh, /auth/mfa/*, /auth/password/*
// Toutes les autres routes /api/v1/* passent par l'intercepteur.
function isAuthEndpoint(path: string): boolean {
  return path.startsWith('/api/v1/auth/');
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: RequestOptions = {},
): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: {
      // N'envoie `Content-Type: application/json` QUE s'il y a un body -
      // Fastify 5 rejette FST_ERR_CTP_EMPTY_JSON_BODY si on l'annonce sans.
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(opts.accessToken ? { Authorization: `Bearer ${opts.accessToken}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // Intercepteur silent refresh : si access token expiré côté serveur
  // (401), on tente un refresh partagé puis on rejoue la requête avec le
  // nouveau token. Skippé si :
  //  - c'est déjà un retry (_isRetry) → évite récursion infinie
  //  - la route est /auth/* → le refresh/login/mfa gèrent leur propre cycle
  if (res.status === 401 && !opts._isRetry && !isAuthEndpoint(path)) {
    try {
      await doSilentRefresh();
    } catch {
      // Refresh KO → store nettoyé, redirection login en cours.
      // On construit une ApiFailure propre pour ne pas polluer les logs
      // d'un rejet non-handled.
      throw new ApiFailure(401, {
        code: 'UNAUTHORIZED',
        message: 'Session expirée, reconnexion requise.',
      });
    }
    const newToken = readStore()?.getAccessToken() ?? undefined;
    return request<T>(method, path, body, { ...opts, accessToken: newToken, _isRetry: true });
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    const errBody = (data as { error?: ApiError } | null)?.error ?? {
      code: 'INTERNAL_ERROR',
      message: 'Request failed',
    };
    if (res.status === 403 && errBody.code === 'PASSWORD_CHANGE_REQUIRED') {
      markPasswordChangeRequired();
    }
    throw new ApiFailure(res.status, errBody);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('POST', path, body, opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('PATCH', path, body, opts),
  delete: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, undefined, opts),
};

// ---------------- Typed auth endpoints ----------------

export interface LoginResponse {
  challengeToken: string;
  mfaEnabled: boolean;
  mfaSetupRequired: boolean;
}

export interface MfaSetupResponse {
  qrCodeDataUrl: string;
  otpauthUrl: string;
  recoveryCodes: string[];
}

export interface AuthenticatedResponse {
  accessToken: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: 'admin' | 'director' | 'manager' | 'collaborator' | 'executive';
    teamId: string | null;
    /** True si l'email fait partie de PROTECTED_ADMIN_EMAILS côté serveur.
     *  Permet à l'UI de conditionner certains contrôles sensibles (toggle
     *  mode démo, etc.) — cf SECURITY.md §21. */
    isProtectedAdmin: boolean;
  };
}

export const authApi = {
  login: (email: string, password: string) =>
    api.post<LoginResponse>('/api/v1/auth/login', { email, password }),
  mfaSetup: (challengeToken: string) =>
    api.post<MfaSetupResponse>('/api/v1/auth/mfa/setup', { challengeToken }),
  mfaVerify: (challengeToken: string, code: string) =>
    api.post<AuthenticatedResponse>('/api/v1/auth/mfa/verify', { challengeToken, code }),
  refresh: () => api.post<AuthenticatedResponse>('/api/v1/auth/refresh', {}),
  logout: () => api.post<void>('/api/v1/auth/logout', {}),
  forgotPassword: (email: string) => api.post<void>('/api/v1/auth/password/forgot', { email }),
  changePassword: (accessToken: string, currentPassword: string, newPassword: string) =>
    api.post<void>(
      '/api/v1/auth/password/change',
      { currentPassword, newPassword },
      { accessToken },
    ),
};

// ---------------------------------------------------------------------------
// Passkeys / WebAuthn (cf SECURITY.md §25)
// ---------------------------------------------------------------------------

export interface PasskeyItem {
  id: string;
  deviceLabel: string;
  transports: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

export const passkeyApi = {
  /** Étape 1 register : récupère les options + challenge serveur. */
  registerOptions: (accessToken: string) =>
    api.post<unknown>('/api/v1/auth/passkey/register/options', {}, { accessToken }),
  /** Étape 2 register : envoie la response WebAuthn + label device. */
  registerVerify: (accessToken: string, response: unknown, deviceLabel: string) =>
    api.post<{ ok: boolean }>(
      '/api/v1/auth/passkey/register/verify',
      { response, deviceLabel },
      { accessToken },
    ),
  /** Étape 1 login : récupère options + challengeId (anonymous). */
  loginOptions: () =>
    api.post<{ options: unknown; challengeId: string }>('/api/v1/auth/passkey/login/options', {}),
  /** Étape 2 login : envoie response signée + challengeId, retourne session. */
  loginVerify: (challengeId: string, response: unknown) =>
    api.post<AuthenticatedResponse>('/api/v1/auth/passkey/login/verify', { challengeId, response }),
  /** Liste des passkeys de l'utilisateur courant. */
  list: (accessToken: string) => api.get<PasskeyItem[]>('/api/v1/auth/passkey', { accessToken }),
  /** Révocation d'une passkey. */
  delete: (accessToken: string, passkeyId: string) =>
    api.delete<void>(`/api/v1/auth/passkey/${passkeyId}`, { accessToken }),
};
